import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import Plot from "react-plotly.js";
import {
  fetchFamilyDetails,
  fetchFamilyConstraints,
  fetchSkuList,
  fetchFamilyForecast,
  fetchFamilyWinrate,
  downloadSkuList,
  downloadFamilyForecast,
  downloadAllSkusForecast,
  downloadFamilyWinrate,
  downloadAllSkusWinrate,
  fetchSimilarSkus,
  fetchConfig,
} from "./api";
import { getKpiChartConfig, getForecastChartConfig, getWinrateChartConfig, aggregateByWeek, aggregateWinrateByWeek, formatPeriod } from "./chartData";
import { useAlertsCount, useAlertsLoading } from "./AlertsContext";
import InfoIcon, { SortableHeader } from "./InfoIcon";
import "./App.css";

const forecastColumns = [
  "MONTH",
  "ACT QTY",
  "ACT QTY Y-1",
  "FCST QTY - σ",
  "FCST QTY (avg)",
  "FCST QTY + σ",
  "ACT VAL Y-1",
  "FCST VALUE - σ",
  "FCST VALUE (avg)",
  "FCST VALUE + σ",
];

const optionalSkuColumns = [
  "SKU family",
  "FC CLASS",
  "ROI",
  "MINIMUM ROI",
  "LQ",
  "ROQ",
  "SAFETY STOCK",
  "ON HAND AVG QTY",
  "SERVICE LEVEL",
  "TARGET FILL RATE",
  "INVENTORY COST",
  "TOTAL COST",
  "REVENUE",
  "COGS",
  "GROSS MARGIN",
  "NET MARGIN",
  "SHELF STOCKOUT RATE",
  "UNIT COST",
  "UNIT PRICE",
  "PNA AVAILABILITY",
  "INBOUND COSTS",
  "OUTBOUND COSTS",
  "CARRY COSTS",
  "MARGIN ADJUSTERS",
  "ORDERS PER YEAR",
  "FCST IN LT",
  "LEAD TIME IN WEEKS",
];

const forecastMap = {
  MONTH: "year_week",
  "ACT QTY": "actual_qty",
  "ACT QTY Y-1": "actual_qty_last_year",
  "FCST QTY - σ": "fcst_qty_lowerbound",
  "FCST QTY (avg)": "fcst_qty_avg",
  "FCST QTY + σ": "fcst_qty_upperbound",
  "ACT VAL Y-1": "actual_val_last_year",
  "FCST VALUE - σ": "fcst_val_lowerbound",
  "FCST VALUE (avg)": "fcst_val_avg",
  "FCST VALUE + σ": "fcst_val_upperbound",
};

const winrateColumns = [
  "MONTH",
  "ACT WR Y-1 RAW",
  "FCST WR (avg) RAW",
  "ACT WR Y-1 SHRINKED",
  "FCST WR (avg) SHRINKED",
];

const winrateMap = {
  MONTH: "year_week",
  "ACT WR Y-1 RAW": "actual_wr_last_year",
  "FCST WR (avg) RAW": "fcst_qty_avg",
  "ACT WR Y-1 SHRINKED": "actual_wr_last_year_shrinked",
  "FCST WR (avg) SHRINKED": "fcst_qty_avg_shrinked",
};

const iconVisible = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="32"
    height="32"
    fill="#f5f5f5"
    viewBox="0 0 256 256"
  >
    <path d="M247.31,124.76c-.35-.79-8.82-19.58-27.65-38.41C194.57,61.26,162.88,48,128,48S61.43,61.26,36.34,86.35C17.51,105.18,9,124,8.69,124.76a8,8,0,0,0,0,6.5c.35.79,8.82,19.57,27.65,38.4C61.43,194.74,93.12,208,128,208s66.57-13.26,91.66-38.34c18.83-18.83,27.3-37.61,27.65-38.4A8,8,0,0,0,247.31,124.76ZM128,168a40,40,0,1,1,40-40A40,40,0,0,1,128,168Z"></path>
  </svg>
);

const iconHidden = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="32"
    height="32"
    fill="#f5f5f5"
    viewBox="0 0 256 256"
  >
    <path d="M96.68,57.87a4,4,0,0,1,2.08-6.6A130.13,130.13,0,0,1,128,48c34.88,0,66.57,13.26,91.66,38.35,18.83,18.83,27.3,37.62,27.65,38.41a8,8,0,0,1,0,6.5c-.35.79-8.82,19.57-27.65,38.4q-4.28,4.26-8.79,8.07a4,4,0,0,1-5.55-.36ZM213.92,210.62a8,8,0,1,1-11.84,10.76L180,197.13A127.21,127.21,0,0,1,128,208c-34.88,0-66.57-13.26-91.66-38.34C17.51,150.83,9,132.05,8.69,131.26a8,8,0,0,1,0-6.5C9,124,17.51,105.18,36.34,86.35a135,135,0,0,1,25-19.78L42.08,45.38A8,8,0,1,1,53.92,34.62Zm-65.49-48.25-52.69-58a40,40,0,0,0,52.69,58Z"></path>
  </svg>
);

const iconPlus = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="32"
    height="32"
    fill="#ffffff"
    viewBox="0 0 256 256"
  >
    <path d="M228,128a12,12,0,0,1-12,12H140v76a12,12,0,0,1-24,0V140H40a12,12,0,0,1,0-24h76V40a12,12,0,0,1,24,0v76h76A12,12,0,0,1,228,128Z"></path>
  </svg>
);

const iconMinus = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="32"
    height="32"
    fill="#ffffff"
    viewBox="0 0 256 256"
  >
    <path d="M228,128a12,12,0,0,1-12,12H40a12,12,0,0,1,0-24H216A12,12,0,0,1,228,128Z"></path>
  </svg>
);

const iconDownload = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    fill="#ffffff"
    viewBox="0 0 256 256"
  >
    <path d="M222,144v64a6,6,0,0,1-6,6H40a6,6,0,0,1-6-6V144a6,6,0,0,1,12,0v58H210V144a6,6,0,0,1,12,0Zm-98.24,4.24a6,6,0,0,0,8.48,0l40-40a6,6,0,0,0-8.48-8.48L134,129.51V32a6,6,0,0,0-12,0v97.51L92.24,99.76a6,6,0,0,0-8.48,8.48Z"></path>
  </svg>
);

// Icone freccia rimosse (Drag & Drop replacement)

const SKU_COLUMN_DEFS = {
  "SKU family": {
    sortKey: "family",
    render: (row) => row.family,
  },
  ROI: {
    sortKey: "roi",
    render: (row, formatPercentage) => formatPercentage(row.roi),
    className: (row) =>
      row.min_roi == null
        ? "roi-value"
        : row.roi >= row.min_roi
          ? "roi-value above-min"
          : "roi-value below-min",
  },
  "MINIMUM ROI": {
    sortKey: "min_roi",
    render: (row, formatPercentage) => formatPercentage(row.min_roi),
  },
  LQ: {
    sortKey: "lq",
    render: (row, _, __, formatNumber) => formatNumber(row.lq),
  },
  ROQ: {
    sortKey: "rq",
    render: (row, _, __, formatNumber) => formatNumber(row.rq),
  },
  "SERVICE LEVEL": {
    sortKey: "service_level",
    render: (row, formatPercentage) => formatPercentage(row.service_level),
  },
  "INVENTORY COST": {
    sortKey: "cost",
    render: (row, _, formatCurrency) => formatCurrency(row.cost),
  },
  "GROSS MARGIN": {
    sortKey: "gross_margin",
    render: (row, _, formatCurrency) => formatCurrency(row.gross_margin),
  },
  "NET MARGIN": {
    sortKey: "net_margin",
    render: (row, _, formatCurrency) => formatCurrency(row.net_margin),
  },
  "SHELF STOCKOUT RATE": {
    sortKey: "shelf_stockout_rate",
    render: (row, formatPercentage) =>
      formatPercentage(row.shelf_stockout_rate),
  },
  "UNIT COST": {
    sortKey: "unit_cost",
    render: (row, _, __, ___, formatUnitCurrency) =>
      formatUnitCurrency(row.unit_cost),
  },
  "UNIT PRICE": {
    sortKey: "unit_price",
    render: (row, _, __, ___, formatUnitCurrency) =>
      formatUnitCurrency(row.unit_price),
  },
  "TOTAL COST": {
    sortKey: "budget",
    render: (row, _, formatCurrency) => formatCurrency(row.budget),
  },
  REVENUE: {
    sortKey: "revenue",
    render: (row, _, formatCurrency) => formatCurrency(row.revenue),
  },
  COGS: {
    sortKey: "cogs",
    render: (row, _, formatCurrency) => formatCurrency(row.cogs),
  },
  "PNA AVAILABILITY": {
    sortKey: "pna_availability",
    render: (row, formatPercentage) => formatPercentage(row.pna_availability),
  },
  "INBOUND COSTS": {
    sortKey: "inbound_costs",
    render: (row, _, formatCurrency) => formatCurrency(row.inbound_costs),
  },
  "OUTBOUND COSTS": {
    sortKey: "outbound_costs",
    render: (row, _, formatCurrency) => formatCurrency(row.outbound_costs),
  },
  "CARRY COSTS": {
    sortKey: "carry_costs",
    render: (row, _, formatCurrency) => formatCurrency(row.carry_costs),
  },
  "MARGIN ADJUSTERS": {
    sortKey: "margin_adjusters",
    render: (row, _, formatCurrency) => formatCurrency(row.margin_adjusters),
  },
  "ON HAND AVG QTY": {
    sortKey: "on_hand_mean",
    render: (row, _, __, formatNumber) => formatNumber(row.on_hand_mean),
  },
  "ORDERS PER YEAR": {
    sortKey: "orders_per_year",
    render: (row, _, __, formatNumber) => formatNumber(row.orders_per_year),
  },
  "FC CLASS": {
    sortKey: "fc_classification",
    render: (row) => row.fc_classification || "N/A",
  },
  "TARGET FILL RATE": {
    sortKey: "target_fill_rate",
    render: (row, formatPercentage) => formatPercentage(row.target_fill_rate),
  },
  "FCST IN LT": {
    sortKey: "fcst_in_lt",
    render: (row, _, __, formatNumber) => formatNumber(row.fcst_in_lt),
  },
  "SAFETY STOCK": {
    sortKey: "safety_stock",
    render: (row, _, __, formatNumber) => formatNumber(row.safety_stock),
  },
  "LEAD TIME IN WEEKS": {
    sortKey: "weekly_lead_time",
    render: (row, _, __, formatNumber) => formatNumber(row.weekly_lead_time),
  },
};

// Questo ci permette di gestire facilmente etichette e formattazione.
const constraintConfig = {
  min_roi: { label: "Minimum ROI", type: "percentage" },
  max_shelf_stockout_rate: {
    label: "Maximum Shelf Stockout Rate",
    type: "percentage",
  },
  min_pna: { label: "Minimum PNA", type: "percentage" },
  min_fill_rate: { label: "Minimum Fill Rate", type: "percentage" },
};

const Pagination = ({ currentPage, totalPages, onPageChange }) => {
  const currentPageUI = currentPage + 1;

  const renderControls = () => {
    if (totalPages <= 1) return null;
    const buttons = [];
    const maxVisibleButtons = 5;

    buttons.push(
      <button
        key="prev"
        className="pagination-btn"
        onClick={() => onPageChange(currentPageUI - 1)}
        disabled={currentPageUI === 1}
      >
        Previous
      </button>,
    );

    let startPage, endPage;
    if (totalPages <= maxVisibleButtons + 2) {
      startPage = 1;
      endPage = totalPages;
    } else {
      if (currentPageUI <= 3) {
        startPage = 1;
        endPage = 5;
      } else if (currentPageUI >= totalPages - 2) {
        startPage = totalPages - 4;
        endPage = totalPages;
      } else {
        startPage = currentPageUI - 2;
        endPage = currentPageUI + 2;
      }
    }

    if (startPage > 1) {
      buttons.push(
        <button
          key={1}
          className="pagination-btn"
          onClick={() => onPageChange(1)}
        >
          1
        </button>,
      );
      if (startPage > 2) {
        buttons.push(
          <span key="start-ellipsis" className="pagination-ellipsis">
            ...
          </span>,
        );
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <button
          key={i}
          className={`pagination-btn ${currentPageUI === i ? "active" : ""}`}
          onClick={() => onPageChange(i)}
        >
          {i}
        </button>,
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        buttons.push(
          <span key="end-ellipsis" className="pagination-ellipsis">
            ...
          </span>,
        );
      }
      buttons.push(
        <button
          key={totalPages}
          className="pagination-btn"
          onClick={() => onPageChange(totalPages)}
        >
          {totalPages}
        </button>,
      );
    }

    buttons.push(
      <button
        key="next"
        className="pagination-btn"
        onClick={() => onPageChange(currentPageUI + 1)}
        disabled={currentPageUI === totalPages}
      >
        Next
      </button>,
    );

    return buttons;
  };

  return <div className="pagination-container">{renderControls()}</div>;
};

const skuConfigMap = {
  sku_family: "SKU family",
  roi: "ROI",
  lq: "LQ",
  roq: "ROQ",
  sl: "SERVICE LEVEL",
  gross_margin: "GROSS MARGIN",
  net_margin: "NET MARGIN",
  cost: "INVENTORY COST",
  budget: "TOTAL COST",
  revenue: "REVENUE",
  cogs: "COGS",
  min_roi: "MINIMUM ROI",
  shelf_stockout_rate: "SHELF STOCKOUT RATE",
  unit_cost: "UNIT COST",
  unit_price: "UNIT PRICE",
  pna_availability: "PNA AVAILABILITY",
  inbound_costs: "INBOUND COSTS",
  outbound_costs: "OUTBOUND COSTS",
  carry_costs: "CARRY COSTS",
  margin_adjusters: "MARGIN ADJUSTERS",
  on_hand_mean: "ON HAND AVG QTY",
  orders_per_year: "ORDERS PER YEAR",
  fc_classification: "FC CLASS",
  target_fill_rate: "TARGET FILL RATE",
  fcst_in_lt: "FCST IN LT",
  safety_stock: "SAFETY STOCK",
  weekly_lead_time: "LEAD TIME IN WEEKS",
};

// Hook per salvare/caricare da localStorage
function useStickyState(defaultValue, key) {
  const [value, setValue] = useState(() => {
    try {
      const stickyValue = window.localStorage.getItem(key);
      return stickyValue !== null ? JSON.parse(stickyValue) : defaultValue;
    } catch (e) {
      console.warn("Error reading from localStorage", e);
      return defaultValue;
    }
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
      console.warn("Error writing to localStorage", e);
    }
  }, [key, value]);

  return [value, setValue];
}

function FamilyView() {
  const alertsCount = useAlertsCount();
  const alertsLoading = useAlertsLoading();
  const { familyId } = useParams();
  const [searchParams] = useSearchParams();
  const from = searchParams.get("from");
  const [config, setConfig] = useState(null);
  const [familyDetails, setFamilyDetails] = useState(null);
  const [familyKpiHistory, setFamilyKpiHistory] = useState(null);
  const [unconstrainedFamilyKpiHistory, setUnconstrainedFamilyKpiHistory] = useState(null);
  const [familyConstraints, setFamilyConstraints] = useState(null);
  const [skuData, setSkuData] = useState([]);
  const [skuViewMode, setSkuViewMode] = useState("constrained");
  const [isSkuCompareActive, setIsSkuCompareActive] = useState(false);
  const [unconstrainedSkuData, setUnconstrainedSkuData] = useState(null);
  const [forecastData, setForecastData] = useState([]);
  const [allForecastData, setAllForecastData] = useState([]);
  const [availableRuns, setAvailableRuns] = useState([]);
  const [selectedGenerationIndex, setSelectedGenerationIndex] = useState(0);
  const [forecastMode, setForecastMode] = useState("demand");
  const [customerType, setCustomerType] = useState("combined");
  const [availabilityType, setAvailabilityType] = useState("available");
  const [winrateData, setWinrateData] = useState([]);
  const [allWinrateData, setAllWinrateData] = useState([]);
  const [winrateRuns, setWinrateRuns] = useState([]);
  const [selectedWinrateGenIndex, setSelectedWinrateGenIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDownloadingSku, setIsDownloadingSku] = useState(false);
  const [isDownloadingForecast, setIsDownloadingForecast] = useState(false);
  const [isDownloadingAllSkus, setIsDownloadingAllSkus] = useState(false);
  const [isDownloadDropdownOpen, setIsDownloadDropdownOpen] = useState(false);
  const [isSkuDownloadDropdownOpen, setIsSkuDownloadDropdownOpen] =
    useState(false);
  const [isDownloadingWinrate, setIsDownloadingWinrate] = useState(false);
  const [isDownloadingAllSkusWinrate, setIsDownloadingAllSkusWinrate] = useState(false);
  const [isWinrateDropdownOpen, setIsWinrateDropdownOpen] = useState(false);
  const skuColsRef = useRef(null);
  const forecastColsRef = useRef(null);
  const [skuFilterInputs, setSkuFilterInputs] = useState([
    {
      id: Date.now(),
      column: "roi",
      operator: ">",
      value: "",
    },
  ]);

  // Ordine colonne SKU (LocalStorage)
  const [skuColumnOrder, setSkuColumnOrder] = useStickyState(
    optionalSkuColumns,
    "familyView_skuColumnOrder_v4",
  );

  // Visibilità colonne SKU (LocalStorage)
  const [skuColumnVisibility, setSkuColumnVisibility] = useStickyState(
    optionalSkuColumns.reduce((acc, col) => ({ ...acc, [col]: true }), {}),
    "familyView_skuVisibility_v4",
  );

  const [isSkuColumnDropdownOpen, setIsSkuColumnDropdownOpen] = useState(false);
  const [popupChart, setPopupChart] = useState(null);
  const [isCompareActive, setIsCompareActive] = useState(false);

  // Visibilità colonne Forecast (LocalStorage)
  const [columnVisibility, setColumnVisibility] = useStickyState(
    (() => {
      const initialState = forecastColumns.reduce(
        (acc, col) => ({ ...acc, [col]: true }),
        {},
      );
      initialState["ACT QTY"] = false;
      return initialState;
    })(),
    "familyView_forecastVisibility_v2",
  );

  const [winrateColumnVisibility, setWinrateColumnVisibility] = useStickyState(
    winrateColumns.reduce((acc, col) => ({ ...acc, [col]: true }), {}),
    "familyView_winrateVisibility_v3",
  );

  const [isColumnDropdownOpen, setIsColumnDropdownOpen] = useState(false);
  const [appliedSkuFilters, setAppliedSkuFilters] = useState({});
  const [skuCurrentPage, setSkuCurrentPage] = useState(0);
  const [skuTotalPages, setSkuTotalPages] = useState(0);
  const [isSkuLoading, setIsSkuLoading] = useState(true);
  const [skuSortConfig, setSkuSortConfig] = useState({
    key: null,
    direction: null,
  });
  const [forecastSortConfig, setForecastSortConfig] = useState({
    key: null,
    direction: null,
  });

  const [skuSuggestions, setSkuSuggestions] = useState([]);
  const [activeSuggestionRowId, setActiveSuggestionRowId] = useState(null);
  const [isSearchingSku, setIsSearchingSku] = useState(false);
  const searchDebounceRef = useRef(null);

  const filteredForecastData = useMemo(
    () => aggregateByWeek(forecastData, customerType),
    [forecastData, customerType]
  );
  const filteredWinrateData = useMemo(
    () => aggregateWinrateByWeek(winrateData, forecastData, customerType),
    [winrateData, forecastData, customerType]
  );

  const [isWinrateCompareActive, setIsWinrateCompareActive] = useState(false);
  const [oppositeWinrateData, setOppositeWinrateData] = useState([]);

  useEffect(() => {
    setOppositeWinrateData([]);
    if (!isWinrateCompareActive || !familyId) return undefined;
    let cancelled = false;
    fetchFamilyWinrate(familyId, {
      customer_type: customerType,
      availability: availabilityType === "available" ? "unavailable" : "available",
    })
      .then((res) => {
        if (!cancelled) setOppositeWinrateData(res?.forecast_result || []);
      })
      .catch((err) => console.error("Failed to fetch comparison win rate:", err));
    return () => {
      cancelled = true;
    };
  }, [isWinrateCompareActive, familyId, customerType, availabilityType]);

  const winrateCompareMap = useMemo(() => {
    if (!isWinrateCompareActive) return null;
    const runId = winrateRuns[selectedWinrateGenIndex]?.run_id;
    const rows = oppositeWinrateData.filter((r) => r.run_id === runId);
    return new Map(
      aggregateWinrateByWeek(rows, forecastData, customerType).map((r) => [r.year_week, r]),
    );
  }, [isWinrateCompareActive, oppositeWinrateData, winrateRuns, selectedWinrateGenIndex, forecastData, customerType]);

  useEffect(() => {
    const reconcile = (setter, columns) =>
      setter((prev) => {
        const next = {};
        let changed = Object.keys(prev).some((key) => !columns.includes(key));
        columns.forEach((col) => {
          if (prev[col] === undefined) changed = true;
          next[col] = prev[col] === undefined ? true : prev[col];
        });
        return changed ? next : prev;
      });
    reconcile(setColumnVisibility, forecastColumns);
    reconcile(setWinrateColumnVisibility, winrateColumns);
  }, [setColumnVisibility, setWinrateColumnVisibility]);

  const showWrCol = (col) => winrateColumnVisibility[col];

  const comparisonData = useMemo(() => {
    if (!isCompareActive) return null;
    if (forecastMode === "demand") {
      if (availableRuns.length === 0) return null;
      const currentRunId = availableRuns[0].run_id;
      return aggregateByWeek(allForecastData.filter((row) => row.run_id === currentRunId), customerType);
    } else {
      if (winrateRuns.length === 0) return null;
      const currentRunId = winrateRuns[0].run_id;
      const compDemand = availableRuns.length > 0
        ? allForecastData.filter((row) => row.run_id === availableRuns[0].run_id)
        : forecastData;
      return aggregateWinrateByWeek(allWinrateData.filter((row) => row.run_id === currentRunId), compDemand, customerType);
    }
  }, [isCompareActive, forecastMode, availableRuns, allForecastData, winrateRuns, allWinrateData, customerType, forecastData]);

  const processForecastData = (rawData) => {
    if (!rawData || rawData.length === 0) return { runs: [], sortedData: [] };

    const runMap = new Map();

    rawData.forEach((row) => {
      if (!runMap.has(row.run_id)) {
        runMap.set(row.run_id, {
          run_id: row.run_id,
          run_date: row.run_date,
          parsedDate: new Date(row.run_date),
        });
      }
    });

    const sortedRuns = Array.from(runMap.values()).sort((a, b) => {
      return b.parsedDate - a.parsedDate;
    });

    return { runs: sortedRuns, sortedData: rawData };
  };

  const loadSkuData = useCallback(
    async (page = 0, filters = {}, sort = { key: null, direction: null }) => {
      setIsSkuLoading(true);

      try {
        const params = {
          ...filters,
          family: familyId,
          page_n: page,
          last_run_only: true,
        };

        if (sort.key && sort.direction) {
          params.order_by = sort.key;
          params.order = sort.direction;
        }

        const skuApiResponse = await fetchSkuList(params);

        if (skuApiResponse?.sku_list && skuApiResponse?.pagination) {
          const constrained = skuApiResponse.sku_list.filter(
            (r) => r.optimization_mode === "constrained",
          );
          const unconstrained = skuApiResponse.sku_list.filter(
            (r) => r.optimization_mode === "unconstrained",
          );
          setSkuData(constrained);
          setUnconstrainedSkuData(unconstrained);
          setSkuCurrentPage(skuApiResponse.pagination.current_page);
          setSkuTotalPages(skuApiResponse.pagination.total_pages);
        } else {
          setSkuData([]);
          setUnconstrainedSkuData(null);
          setSkuCurrentPage(0);
          setSkuTotalPages(0);
        }
      } catch (error) {
        console.error("Failed to fetch SKU list:", error);
        setSkuData([]);
        setUnconstrainedSkuData(null);
      } finally {
        setIsSkuLoading(false);
      }
    },
    [familyId],
  );

  const unconstrainedSkuMap = useMemo(() => {
    if (!unconstrainedSkuData) return new Map();
    const map = new Map();
    unconstrainedSkuData.forEach((r) => map.set(r.sku_id, r));
    return map;
  }, [unconstrainedSkuData]);

  const hasSkuUnconstrained =
    unconstrainedSkuData !== null && unconstrainedSkuData.length > 0;

  const effectiveSkuData = useMemo(() => {
    if (skuViewMode === "unconstrained" && unconstrainedSkuData) {
      return unconstrainedSkuData;
    }
    return skuData;
  }, [skuData, unconstrainedSkuData, skuViewMode]);

  const handleSelectSkuSuggestion = (rowId, selectedSku) => {
    // Aggiorna input visivo
    setSkuFilterInputs((prev) =>
      prev.map((row) =>
        row.id === rowId ? { ...row, value: selectedSku } : row,
      ),
    );
    // Chiudi suggerimenti
    setSkuSuggestions([]);
    setActiveSuggestionRowId(null);

    // Applica filtro immediatamente
    const updatedInputs = skuFilterInputs.map((row) =>
      row.id === rowId ? { ...row, value: selectedSku } : row,
    );
    applySkuFiltersInternal(updatedInputs);
  };

  useEffect(() => {
    const loadFamilyData = async () => {
      setLoading(true);
      setError(null);
      try {
        const configData = await fetchConfig();
        setConfig(configData);

        // Usiamo allSettled per non bloccare tutto se una API fallisce
        const results = await Promise.allSettled([
          fetchFamilyDetails(familyId),
          fetchFamilyConstraints(familyId),
        ]);

        const detailsResult = results[0];
        const constraintsResult = results[1];

        // 1. Gestione Dettagli
        let detailsRes = {};
        if (detailsResult.status === "fulfilled") {
          detailsRes = detailsResult.value;
        } else {
          throw new Error(
            `Failed to load Family Details: ${detailsResult.reason}`,
          );
        }

        // Gestione Constraints
        let constraintsRes = {};
        if (constraintsResult.status === "fulfilled") {
          constraintsRes = constraintsResult.value;
        } else {
          console.warn(
            "Constraints API failed, continuing without constraints data.",
            constraintsResult.reason,
          );
          constraintsRes = { family_constraints: [] };
        }

        // Elaborazione "Expected Value & History"
        const rawDetails = detailsRes.family_details || [];

        const prepareHistoryData = (items) =>
          items
            .filter((item) => item.run_date && item.run_id)
            .map((item) => ({ ...item, parsedDate: new Date(item.run_date) }))
            .sort((a, b) => a.parsedDate - b.parsedDate);

        const constrainedHistoryData = prepareHistoryData(
          rawDetails.filter((item) => item.optimization_mode === "constrained"),
        );
        const unconstrainedHistoryData = prepareHistoryData(
          rawDetails.filter((item) => item.optimization_mode === "unconstrained"),
        );

        const buildFamilyHistory = (sortedData) => ({
          roi: sortedData.map((d) => d.roi?.current),
          sl: sortedData.map((d) => d.service_level?.current),
          netMargin: sortedData.map((d) => d.net_margin?.current),
          grossMargin: sortedData.map((d) => d.gross_margin?.current),
          cost: sortedData.map((d) => d.cost),
          pna: sortedData.map((d) => d.pna_availability),
          runLabels: sortedData.map((d) => {
            try {
              return d.parsedDate.toISOString().split("T")[0];
            } catch (e) {
              return d.run_id;
            }
          }),
          rawData: sortedData,
        });

        if (constrainedHistoryData.length > 0) {
          setFamilyDetails(constrainedHistoryData[constrainedHistoryData.length - 1]);
          setFamilyKpiHistory(buildFamilyHistory(constrainedHistoryData));
        } else {
          setFamilyDetails(null);
          setFamilyKpiHistory(null);
        }

        if (unconstrainedHistoryData.length > 0) {
          setUnconstrainedFamilyKpiHistory(buildFamilyHistory(unconstrainedHistoryData));
        } else {
          setUnconstrainedFamilyKpiHistory(null);
        }

        // Gestione Constraints (Sicura anche se l'API è fallita)
        if (constraintsRes?.family_constraints?.length > 0) {
          setFamilyConstraints(constraintsRes.family_constraints[0]);
        } else {
          setFamilyConstraints({});
        }

      } catch (err) {
        console.error("Error loading critical family data:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    loadFamilyData();
  }, [familyId]);

  const [isForecastLoading, setIsForecastLoading] = useState(false);
  useEffect(() => {
    if (!familyId) return;
    const loadForecastAndWinrate = async () => {
      setIsForecastLoading(true);
      const [fResult, wResult] = await Promise.allSettled([
        fetchFamilyForecast(familyId, { customer_type: customerType }),
        fetchFamilyWinrate(familyId, { customer_type: customerType, availability: availabilityType }),
      ]);

      if (fResult.status === "fulfilled" && fResult.value?.forecast_result) {
        const rawRows = fResult.value.forecast_result;
        const { runs } = processForecastData(rawRows);
        setAllForecastData(rawRows);
        setAvailableRuns(runs);
        if (runs.length > 0) {
          const currentRunId = runs[0].run_id;
          setForecastData(rawRows.filter((r) => r.run_id === currentRunId));
          setSelectedGenerationIndex(0);
        } else {
          setForecastData([]);
        }
      } else {
        setForecastData([]);
        setAllForecastData([]);
        setAvailableRuns([]);
      }

      if (wResult.status === "fulfilled" && wResult.value?.forecast_result) {
        const rawRows = wResult.value.forecast_result;
        const { runs } = processForecastData(rawRows);
        setAllWinrateData(rawRows);
        setWinrateRuns(runs);
        if (runs.length > 0) {
          setWinrateData(rawRows.filter((r) => r.run_id === runs[0].run_id));
          setSelectedWinrateGenIndex(0);
        } else {
          setWinrateData([]);
        }
      } else {
        setWinrateData([]);
        setAllWinrateData([]);
        setWinrateRuns([]);
      }
      setIsForecastLoading(false);
    };
    loadForecastAndWinrate();
  }, [familyId, customerType, availabilityType]);

  useEffect(() => {
    loadSkuData(skuCurrentPage, appliedSkuFilters, skuSortConfig);
  }, [skuCurrentPage, appliedSkuFilters, skuSortConfig, loadSkuData]);

  // *** CHIUDE IL DROPDOWN SE SI CLICCA FUORI ***
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        (isDownloadDropdownOpen || isSkuDownloadDropdownOpen) &&
        !event.target.closest(".split-btn-container")
      ) {
        setIsDownloadDropdownOpen(false);
        setIsSkuDownloadDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDownloadDropdownOpen, isSkuDownloadDropdownOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isWinrateDropdownOpen &&
        !event.target.closest(".split-btn-container")
      ) {
        setIsWinrateDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isWinrateDropdownOpen]);

  // Effetto per applicare la visibilità di default dal config.json (se non salvata in localStorage)
  useEffect(() => {
    if (!config) return;

    const hasSavedSkuConfig = window.localStorage.getItem(
      "familyView_skuVisibility",
    );

    const skuColsConfig = config.pageLayouts?.family?.skuTableColumns;

    if (skuColsConfig && !hasSavedSkuConfig) {
      setSkuColumnVisibility((prev) => {
        const newVisibility = { ...prev };
        skuColsConfig.forEach((col) => {
          const mappedName = skuConfigMap[col.id];
          if (mappedName) {
            newVisibility[mappedName] = col.visible;
          }
        });
        return newVisibility;
      });
    }

    // --- AUTO-AGGIORNAMENTO COLONNE SKU ---
    // Inietta automaticamente le nuove colonne negli utenti che hanno un localStorage vecchio.

    // 1. Aggiorna l'ordine
    setSkuColumnOrder((prevOrder) => {
      const cleanOrder = prevOrder.filter((col) =>
        optionalSkuColumns.includes(col),
      );
      const missingColumns = optionalSkuColumns.filter(
        (col) => !cleanOrder.includes(col),
      );

      if (cleanOrder.length !== prevOrder.length || missingColumns.length > 0) {
        return [...cleanOrder, ...missingColumns];
      }
      return prevOrder;
    });

    // 2. Aggiorna la visibilità
    setSkuColumnVisibility((prevVis) => {
      let hasChanges = false;
      const newVisibility = {};

      // Mantiene solo le chiavi valide
      Object.keys(prevVis).forEach((key) => {
        if (optionalSkuColumns.includes(key)) {
          newVisibility[key] = prevVis[key];
        } else {
          hasChanges = true;
        }
      });

      optionalSkuColumns.forEach((col) => {
        if (newVisibility[col] === undefined) {
          newVisibility[col] = true;
          hasChanges = true;
        }
      });

      return hasChanges ? newVisibility : prevVis;
    });
  }, [config, setSkuColumnVisibility, setSkuColumnOrder]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (skuColsRef.current && !skuColsRef.current.contains(event.target)) {
        setIsSkuColumnDropdownOpen(false);
      }
      if (
        forecastColsRef.current &&
        !forecastColsRef.current.contains(event.target)
      ) {
        setIsColumnDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleDownloadSku = async () => {
    setIsDownloadingSku(true);
    try {
      const downloadParams = {
        ...appliedSkuFilters,
        family: familyId,
        order_by: skuSortConfig.key || "sku_id",
        order: skuSortConfig.direction || "asc",
        last_run_only: true,
        optimization_mode: skuViewMode,
      };

      const filename = `sku_overview_${familyId}_${skuViewMode}.xlsx`;

      await downloadSkuList(downloadParams, filename);
    } catch (error) {
      console.error(error);
      alert("Error downloading SKU file.");
    } finally {
      setIsDownloadingSku(false);
    }
  };

  const handleDownloadSkuUnfiltered = async () => {
    setIsSkuDownloadDropdownOpen(false);
    setIsDownloadingSku(true);
    try {
      const downloadParams = {
        family: familyId,
        order_by: skuSortConfig.key || "sku_id",
        order: skuSortConfig.direction || "asc",
        last_run_only: true,
        optimization_mode: skuViewMode,
      };

      const filename = `sku_overview_${familyId}_${skuViewMode}_unfiltered.xlsx`;

      await downloadSkuList(downloadParams, filename);
    } catch (error) {
      console.error(error);
      alert("Error downloading SKU file.");
    } finally {
      setIsDownloadingSku(false);
    }
  };

  const handleDownloadForecast = async () => {
    // Verifica che ci sia una run selezionata
    if (!availableRuns[selectedGenerationIndex]) {
      alert("No generation selected to download.");
      return;
    }

    const currentRun = availableRuns[selectedGenerationIndex];
    const currentRunId = currentRun.run_id;

    // Nome file pulito
    const filename = `forecast_${familyId}_${currentRunId}_${customerType}.xlsx`;

    setIsDownloadingForecast(true);
    try {
      // Passiamo familyId e il run_id
      await downloadFamilyForecast(
        familyId,
        { run_id: currentRunId, customer_type: customerType },
        filename,
      );
    } catch (error) {
      console.error(error);
      alert("Error downloading forecast file.");
    } finally {
      setIsDownloadingForecast(false);
    }
  };

  const handleDownloadForecastUnfiltered = async () => {
    setIsDownloadDropdownOpen(false);
    if (!availableRuns[selectedGenerationIndex]) {
      alert("No generation selected to download.");
      return;
    }

    const currentRunId = availableRuns[selectedGenerationIndex].run_id;
    const filename = `forecast_${familyId}_${currentRunId}_unfiltered.xlsx`;

    setIsDownloadingForecast(true);
    try {
      await downloadFamilyForecast(familyId, { run_id: currentRunId }, filename);
    } catch (error) {
      console.error(error);
      alert("Error downloading forecast file.");
    } finally {
      setIsDownloadingForecast(false);
    }
  };

  // *** HANDLE DOWNLOAD ALL SKUS ***
  const handleDownloadAllSkus = async () => {
    if (!availableRuns[selectedGenerationIndex]) {
      alert("No generation selected to download.");
      return;
    }

    const currentRun = availableRuns[selectedGenerationIndex];
    const currentRunId = currentRun.run_id;

    setIsDownloadingAllSkus(true);

    try {
      const filename = `family_${familyId}_skus_forecast_${currentRunId}_unfiltered.xlsx`;

      await downloadAllSkusForecast(
        {
          run_id: currentRunId,
          family: familyId,
        },
        filename,
      );
    } catch (error) {
      console.error(error);
      alert("Error downloading all SKUs file.");
    } finally {
      setIsDownloadingAllSkus(false);
    }
  };

  const handleDownloadWinrate = async () => {
    if (!winrateRuns[selectedWinrateGenIndex]) {
      alert("No generation selected to download.");
      return;
    }

    const currentRun = winrateRuns[selectedWinrateGenIndex];
    const currentRunId = currentRun.run_id;
    const filename = `winrate_${familyId}_${currentRunId}_${customerType}_${availabilityType}.xlsx`;

    setIsDownloadingWinrate(true);
    try {
      await downloadFamilyWinrate(familyId, { run_id: currentRunId, customer_type: customerType, availability: availabilityType }, filename);
    } catch (error) {
      console.error(error);
      alert("Error downloading win rate file.");
    } finally {
      setIsDownloadingWinrate(false);
    }
  };

  const handleDownloadWinrateUnfiltered = async () => {
    setIsWinrateDropdownOpen(false);
    if (!winrateRuns[selectedWinrateGenIndex]) {
      alert("No generation selected to download.");
      return;
    }

    const currentRunId = winrateRuns[selectedWinrateGenIndex].run_id;
    const filename = `winrate_${familyId}_${currentRunId}_unfiltered.xlsx`;

    setIsDownloadingWinrate(true);
    try {
      await downloadFamilyWinrate(familyId, { run_id: currentRunId, availability: "all" }, filename);
    } catch (error) {
      console.error(error);
      alert("Error downloading win rate file.");
    } finally {
      setIsDownloadingWinrate(false);
    }
  };

  const handleDownloadAllSkusWinrate = async () => {
    if (!winrateRuns[selectedWinrateGenIndex]) {
      alert("No generation selected to download.");
      return;
    }

    const currentRun = winrateRuns[selectedWinrateGenIndex];
    const currentRunId = currentRun.run_id;

    setIsDownloadingAllSkusWinrate(true);
    try {
      const filename = `family_${familyId}_skus_winrate_${currentRunId}_unfiltered.xlsx`;
      await downloadAllSkusWinrate(
        { run_id: currentRunId, family: familyId, availability: "all" },
        filename,
      );
    } catch (error) {
      console.error(error);
      alert("Error downloading all SKUs win rate file.");
    } finally {
      setIsDownloadingAllSkusWinrate(false);
    }
  };

  const handleSkuPageChange = (page) => {
    const pageN = page - 1;
    setSkuCurrentPage(pageN); // L'useEffect si occuperà di ricaricare i dati
    document
      .querySelector(".sku-overview-section")
      ?.scrollIntoView({ behavior: "smooth" });
  };

  // FUNZIONE: Gestisce lo spostamento tramite Drag & Drop (SKU)
  const handleSkuOnDrop = (draggedIndex, targetIndex) => {
    if (draggedIndex === targetIndex) return;

    const newOrder = [...skuColumnOrder];
    const draggedItem = newOrder[draggedIndex];

    newOrder.splice(draggedIndex, 1);
    newOrder.splice(targetIndex, 0, draggedItem);

    setSkuColumnOrder(newOrder);
  };

  // Gestione click intestazione (Asc -> Desc -> Null)
  const requestSkuSort = (key) => {
    let direction = "asc";

    if (skuSortConfig.key === key) {
      if (skuSortConfig.direction === "asc") {
        direction = "desc";
      } else if (skuSortConfig.direction === "desc") {
        direction = null; // Reset
      }
    }

    setSkuSortConfig({ key: direction ? key : null, direction });
    setSkuCurrentPage(0); // Torna sempre alla prima pagina
  };

  // Indicatore freccia
  const getSkuSortIndicator = (key) => {
    if (skuSortConfig.key !== key) return null;
    return skuSortConfig.direction === "asc" ? " ▲" : " ▼";
  };

  // *** Handler per applicare i filtri ***
  const handleSkuFilterRowChange = (id, field, newValue) => {
    setSkuFilterInputs((prev) =>
      prev.map((row) => (row.id === id ? { ...row, [field]: newValue } : row)),
    );

    const currentRow = skuFilterInputs.find((r) => r.id === id);
    const isSkuColumn =
      (field === "column" && newValue === "sku_id") ||
      (currentRow && currentRow.column === "sku_id");

    if (isSkuColumn && field === "value") {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }

      if (!newValue || newValue.trim() === "") {
        setSkuSuggestions([]);
        return;
      }

      setIsSearchingSku(true);
      setActiveSuggestionRowId(id);

      searchDebounceRef.current = setTimeout(async () => {
        try {
          const response = await fetchSimilarSkus(newValue);
          if (response && response.skus) {
            setSkuSuggestions(response.skus);
          } else {
            setSkuSuggestions([]);
          }
        } catch (err) {
          console.error("Error searching SKUs", err);
          setSkuSuggestions([]);
        } finally {
          setIsSearchingSku(false);
        }
      }, 750);
    } else {
      if (field === "column" && newValue !== "sku_id") {
        setSkuSuggestions([]);
        setActiveSuggestionRowId(null);
      }
    }
  };

  // Aggiunge una nuova riga
  const addSkuFilterRow = () => {
    setSkuFilterInputs((prev) => [
      ...prev,
      { id: Date.now(), column: "roi", operator: ">", value: "" },
    ]);
  };

  // Rimuove una riga e applica i filtri rimanenti
  const removeSkuFilterRow = (id) => {
    const newInputs = skuFilterInputs.filter((row) => row.id !== id);
    setSkuFilterInputs(newInputs);
    applySkuFiltersInternal(newInputs);
  };

  const sortedForecastData = useMemo(() => {
    // Filtriamo prima i dati
    let processedData = [...filteredForecastData].filter((row) => {
      return (
        (row.fcst_qty_avg != null && row.fcst_qty_avg !== 0) ||
        (row.fcst_qty_lowerbound != null && row.fcst_qty_lowerbound !== 0) ||
        (row.fcst_qty_upperbound != null && row.fcst_qty_upperbound !== 0) ||
        (row.fcst_val_avg != null && row.fcst_val_avg !== 0) ||
        (row.fcst_val_lowerbound != null && row.fcst_val_lowerbound !== 0) ||
        (row.fcst_val_upperbound != null && row.fcst_val_upperbound !== 0) ||
        (row.actual_qty_last_year != null && row.actual_qty_last_year !== 0) ||
        (row.actual_val_last_year != null && row.actual_val_last_year !== 0)
      );
    });

    // Gestione Ordinamento
    if (forecastSortConfig.key && forecastSortConfig.direction) {
      // Recuperiamo la chiave reale dal mapping
      const dataKey = forecastMap[forecastSortConfig.key];

      processedData.sort((a, b) => {
        let aValue = a[dataKey];
        let bValue = b[dataKey];

        // Gestione valori nulli/undefined
        if (aValue == null)
          aValue = typeof bValue === "string" ? "" : -Infinity;
        if (bValue == null)
          bValue = typeof aValue === "string" ? "" : -Infinity;

        if (aValue < bValue)
          return forecastSortConfig.direction === "asc" ? -1 : 1;
        if (aValue > bValue)
          return forecastSortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    } else {
      // Ordinamento di Default (per Data/Week crescente) se nessun sort è attivo
      processedData.sort((a, b) => a.year_week.localeCompare(b.year_week));
    }

    return processedData;
  }, [filteredForecastData, forecastSortConfig]);

  const sortedWinrateData = useMemo(() => {
    let processedData = [...filteredWinrateData].filter(
      (row) => (row.fcst_qty_avg != null && row.fcst_qty_avg !== 0) || (row.actual_wr_last_year != null && row.actual_wr_last_year !== 0)
    );
    if (forecastSortConfig.key && forecastSortConfig.direction) {
      const dataKey = winrateMap[forecastSortConfig.key];
      if (dataKey) {
        processedData.sort((a, b) => {
          let aValue = a[dataKey];
          let bValue = b[dataKey];
          if (aValue == null) aValue = -Infinity;
          if (bValue == null) bValue = -Infinity;
          if (aValue < bValue) return forecastSortConfig.direction === "asc" ? -1 : 1;
          if (aValue > bValue) return forecastSortConfig.direction === "asc" ? 1 : -1;
          return 0;
        });
      }
    } else {
      processedData.sort((a, b) => a.year_week.localeCompare(b.year_week));
    }
    return processedData;
  }, [filteredWinrateData, forecastSortConfig]);

  // Funzione Trigger Click
  const requestForecastSort = (colName) => {
    let direction = "asc";
    if (
      forecastSortConfig.key === colName &&
      forecastSortConfig.direction === "asc"
    ) {
      direction = "desc";
    } else if (
      forecastSortConfig.key === colName &&
      forecastSortConfig.direction === "desc"
    ) {
      direction = null; // Reset
    }
    setForecastSortConfig({ key: direction ? colName : null, direction });
  };

  // Indicatore Freccia
  const getForecastSortIndicator = (colName) => {
    if (forecastSortConfig.key !== colName) return null;
    return forecastSortConfig.direction === "asc" ? " ▲" : " ▼";
  };

  // Logica interna per costruire l'oggetto filtri API
  const applySkuFiltersInternal = (inputs) => {
    const filters = {};

    inputs.forEach(({ column, operator, value }) => {
      if (column === "sku_id") {
        if (value && value.trim() !== "") {
          filters["sku_id"] = value.trim();
        }
      } else {
        const cleanedValue = String(value)
          .replace(/[%$]/g, "")
          .replace(/,/g, "");
        if (cleanedValue.trim() !== "" && !isNaN(parseFloat(cleanedValue))) {
          const numericValue = parseFloat(cleanedValue);
          if (operator === ">") {
            filters[`${column}_min`] = numericValue;
          } else if (operator === "<") {
            filters[`${column}_max`] = numericValue;
          }
        }
      }
    });

    setSkuCurrentPage(0);
    setAppliedSkuFilters(filters);
  };

  // Wrapper per il bottone Apply
  const handleApplySkuFilters = () => {
    applySkuFiltersInternal(skuFilterInputs);
  };

  // Reset dei filtri
  const resetSkuFilters = () => {
    setSkuFilterInputs([
      {
        id: Date.now(),
        column: "roi",
        operator: ">",
        value: "",
      },
    ]);
    setSkuCurrentPage(0);
    setAppliedSkuFilters({});
  };

  const formatCurrency = (num) => {
    if (num === null || num === undefined) return "N/A";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(num);
  };

  const formatUnitCurrency = (num) => {
    if (num === null || num === undefined) return "N/A";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return "N/A";
    return Math.round(Number(num)).toLocaleString("en-US");
  };

  const formatPercentage = (num) => {
    if (num === null || num === undefined || typeof num !== "number") {
      return "N/A";
    }
    return `${num.toFixed(2)}%`;
  };

  const COST_FIELDS = new Set([
    "budget",
    "cost",
    "cogs",
    "shelf_stockout_rate",
    "inbound_costs",
    "outbound_costs",
    "carry_costs",
    "unit_cost",
    "margin_adjusters",
    "safety_stock",
  ]);

  const renderCompareCell = (def, baseRow, targetRow, ...fmts) => {
    if (!targetRow || targetRow === baseRow) return def.render(baseRow, ...fmts);

    const rawBase = baseRow[def.sortKey];
    const rawTarget = targetRow[def.sortKey];

    if (rawBase == null || rawTarget == null || typeof rawBase !== "number") {
      return def.render(baseRow, ...fmts);
    }

    const baseVal = def.render(baseRow, ...fmts);
    const targetVal = def.render(targetRow, ...fmts);
    const delta = rawTarget - rawBase;
    const isImprovement = COST_FIELDS.has(def.sortKey)
      ? delta < 0
      : delta > 0;
    const deltaClass =
      delta === 0
        ? "compare-neutral"
        : isImprovement
          ? "compare-improvement"
          : "compare-degradation";
    const sign = delta > 0 ? "+" : "";
    const deltaLabel =
      delta === 0
        ? "0%"
        : rawBase !== 0
          ? `${sign}${((delta / Math.abs(rawBase)) * 100).toFixed(1)}%`
          : "—";

    const tooltip = `${baseVal} → ${targetVal} (${deltaLabel})`;

    return (
      <span className="compare-cell-content" title={tooltip}>
        <span className="compare-target">{targetVal}</span>
        <span className={`compare-delta ${deltaClass}`}>{deltaLabel}</span>
      </span>
    );
  };

  const renderWinrateCell = (row, field) => {
    const format = (v) => (v != null ? (v * 100).toFixed(1) + "%" : "—");
    const base = row[field];
    const target = isWinrateCompareActive ? winrateCompareMap?.get(row.year_week)?.[field] : null;
    if (base == null || target == null) return format(base);
    const delta = target - base;
    const deltaClass =
      delta === 0 ? "compare-neutral" : delta > 0 ? "compare-improvement" : "compare-degradation";
    const deltaLabel =
      delta === 0
        ? "0%"
        : base !== 0
          ? `${delta > 0 ? "+" : ""}${((delta / Math.abs(base)) * 100).toFixed(1)}%`
          : "—";
    return (
      <span
        className="compare-cell-content"
        title={
          availabilityType === "available"
            ? `Available ${format(base)} → Unavailable ${format(target)} (${deltaLabel})`
            : `Unavailable ${format(base)} → Available ${format(target)} (${deltaLabel})`
        }
      >
        <span className="compare-target">{format(base)}</span>
        <span className={`compare-delta ${deltaClass}`}>{deltaLabel}</span>
      </span>
    );
  };

  const handleChartClick = (e, type, name, title) => {
    if (
      e &&
      e.target &&
      (e.target.closest(".modebar") || e.target.closest(".modebar-btn"))
    ) {
      return;
    }
    setPopupChart({ type, name, title });
  };
  const closePopup = () => setPopupChart(null);

  // *** Funzione per gestire la classe di colore dei KPI ***
  const getFamilyKpiColorClass = (kpiId) => {
    // Se non ci sono dati, non assegnare colori
    if (!familyDetails || !familyConstraints) return "";

    switch (kpiId) {
      case "roi":
        if (familyDetails.roi.min_roi == null) return ""; // == null per catchare sia null che undefined
        return familyDetails.roi.current >= familyDetails.roi.min_roi
          ? "positive-value"
          : "negative-value";
      case "budget":
        if (familyDetails.max_budget == null) return "";
        return familyDetails.cost <= familyDetails.max_budget
          ? "positive-value"
          : "negative-value";
      case "pna":
        if (familyConstraints.min_pna == null) return "";
        return familyDetails.pna_availability >= familyConstraints.min_pna
          ? "positive-value"
          : "negative-value";
      case "sl":
        return "";
      default:
        return "";
    }
  };

  // Funzione helper per formattare i valori dei vincoli in base al tipo
  const formatConstraintValue = (value, type) => {
    if (typeof value !== "number") return "N/A";
    switch (type) {
      case "currency":
        return formatCurrency(value);
      case "percentage":
        return `${value.toFixed(2)}%`;
      default:
        return formatNumber(value);
    }
  };

  if (loading)
    return (
      <div className="loading-screen">
        Loading Family View for {familyId}...
      </div>
    );
  if (error) return <div className="error-screen">{error}</div>;
  if (!familyDetails || !config || !familyConstraints)
    return <div className="loading-screen">Waiting for data...</div>;

  const kpiDisplayData = {
    roi: {
      title: "ROI",
      value: formatPercentage(familyDetails.roi.current),
      limit: `(Min: ${formatPercentage(familyDetails.roi.min_roi)})`,
    },
    sl: {
      title: "SERVICE LEVEL",
      value: formatPercentage(familyDetails.service_level.current),
    },
    netMargin: {
      title: "NET MARGIN",
      value: formatCurrency(familyDetails.net_margin.current),
    },
    grossMargin: {
      title: "GROSS MARGIN",
      value: formatCurrency(familyDetails.gross_margin.current),
    },
    budget: {
      title: "TOTAL COST",
      value: formatCurrency(familyDetails.cost),
    },
    pna: {
      title: "PNA AVAILABILITY",
      value: formatPercentage(familyDetails.pna_availability),
      limit: familyConstraints.min_pna
        ? `(Min: ${formatPercentage(familyConstraints.min_pna)})`
        : null,
    },
  };

  const kpiConfig = config.pageLayouts.family.kpiBoxes.filter((k) => k.visible);

  return (
    <>
      <div className={popupChart ? "blur-background" : ""}>
        <header>
          <h1>FAMILY VIEW</h1>
          <div className="header-buttons">
            {from === "alerts" && (
              <Link to="/alerts" className="header-btn alerts-btn">
                {alertsLoading ? (
                  <>
                    ALERTS{" "}
                    <span
                      className="alerts-count-skeleton"
                      role="status"
                      aria-label="Loading alerts"
                    />
                  </>
                ) : (
                  `ALERTS (${alertsCount})`
                )}
              </Link>
            )}
            <Link
              to={`/forecast?level=FAMILY&entity=${familyId}`}
              className="header-btn"
            >
              FORECAST
            </Link>
            <Link to="/" className="header-btn">
              GLOBAL VIEW
            </Link>
          </div>
        </header>

        <main className="dashboard-container">
          <div className="family-info">
            <p>
              <strong>SKU Family:</strong> {familyDetails.family}
            </p>
            <p>
              <strong>Family Description:</strong>{" "}
              {familyDetails.family_description}
            </p>
          </div>
          <section className="bordered-section">
            <h2>EXPECTED VALUE & HISTORY</h2>
            <div className="kpi-container">
              {kpiConfig.map(({ id }) => {
                const kpi = kpiDisplayData[id];
                if (!kpi) return null;
                const chartConfig = getKpiChartConfig(
                  id,
                  familyKpiHistory,
                  familyConstraints,
                  false,
                  config,
                  unconstrainedFamilyKpiHistory,
                );

                return (
                  <div className="kpi-box" data-kpi-id={id} key={id}>
                    <div className="kpi-title">
                      <InfoIcon label={kpi.title} />
                    </div>
                    {/* *** Usiamo la nuova funzione per impostare la classe *** */}
                    <div className={`kpi-value ${getFamilyKpiColorClass(id)}`}>
                      <span>
                        Current: <strong>{kpi.value}</strong>
                      </span>
                      {kpi.limit && (
                        <span className="kpi-min-value">{kpi.limit}</span>
                      )}
                    </div>
                    <div
                      className="graph-placeholder"
                      onClick={(e) =>
                        handleChartClick(e, "kpi", id, `${kpi.title} EVOLUTION`)
                      }
                    >
                      <Plot
                        data={chartConfig.traces}
                        layout={chartConfig.layout}
                        config={chartConfig.config}
                        style={{ width: "100%", height: "100%" }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          <div className="split-section-container split-section-container--single">
            {/* BOX 1: FAMILY CONSTRAINTS */}
            <div className="split-box">
              <h2>FAMILY CONSTRAINTS</h2>
              <div className="split-box-content">
                {config.pageLayouts.family.constraintItems
                  .filter((item) => item.visible)
                  .map(({ id }) => {
                    const apiKey = id.replace(
                      /[A-Z]/g,
                      (letter) => `_${letter.toLowerCase()}`,
                    );

                    if (
                      familyConstraints.hasOwnProperty(apiKey) &&
                      constraintConfig.hasOwnProperty(apiKey)
                    ) {
                      const { label, type } = constraintConfig[apiKey];
                      const value = familyConstraints[apiKey];
                      return (
                        <div className="split-item" key={id}>
                          {/* Label a sinistra */}
                          <span>
                            <InfoIcon label={label} />
                          </span>
                          {/* Valore a destra */}
                          <span>{formatConstraintValue(value, type)}</span>
                        </div>
                      );
                    }
                    return null;
                  })}
              </div>
            </div>
          </div>

          <section className="sku-overview-section">
            <h2 className="section-title-container">OVERVIEW BY SKU</h2>
            <div className="constraint-controls">
              <div className="constraint-tabs">
                <button
                  className={`constraint-tab ${skuViewMode === "constrained" ? "active" : ""}`}
                  onClick={() => setSkuViewMode("constrained")}
                >
                  Constrained
                </button>
                <button
                  className={`constraint-tab ${skuViewMode === "unconstrained" ? "active" : ""}`}
                  onClick={() => setSkuViewMode("unconstrained")}
                >
                  Unconstrained
                </button>
              </div>
              <label
                className="compare-switch"
                title={
                  skuViewMode === "constrained"
                    ? "Compare Constrained → Unconstrained"
                    : "Compare Unconstrained → Constrained"
                }
              >
                <input
                  type="checkbox"
                  checked={isSkuCompareActive}
                  disabled={!hasSkuUnconstrained}
                  onChange={() =>
                    setIsSkuCompareActive((prev) => !prev)
                  }
                />
                <span className="compare-slider" />
                <span className="compare-switch-label">Compare</span>
              </label>
            </div>
            <div className="filter-section">
              {skuFilterInputs.map((row, index) => {
                const isLast = index === skuFilterInputs.length - 1;
                const showMinus = skuFilterInputs.length > 1;

                return (
                  <div key={row.id} className="filter-row">
                    {/* Label allineata */}
                    <span className="filter-label">
                      {index === 0 ? "Filter by" : "AND"}
                    </span>

                    {/* Select Column */}
                    <select
                      value={row.column}
                      onChange={(e) =>
                        handleSkuFilterRowChange(
                          row.id,
                          "column",
                          e.target.value,
                        )
                      }
                    >
                      <option value="sku_id">SKU ID</option>
                      <option value="roi">ROI</option>
                      <option value="lq">LQ</option>
                      <option value="rq">ROQ</option>
                      <option value="service_level">SERVICE LEVEL</option>
                    </select>

                    {/* Select Operator */}
                    <select
                      value={row.operator}
                      onChange={(e) =>
                        handleSkuFilterRowChange(
                          row.id,
                          "operator",
                          e.target.value,
                        )
                      }
                      disabled={row.column === "sku_id"}
                      style={row.column === "sku_id" ? { opacity: 0.5 } : {}}
                    >
                      <option value=">">&gt;</option>
                      <option value="<">&lt;</option>
                    </select>

                    <div className="filter-input-wrapper">
                      <input
                        type="text"
                        placeholder={
                          row.column === "sku_id"
                            ? "Type SKU..."
                            : "Enter value..."
                        }
                        value={row.value}
                        onChange={(e) =>
                          handleSkuFilterRowChange(
                            row.id,
                            "value",
                            e.target.value,
                          )
                        }
                        onBlur={() =>
                          setTimeout(() => setActiveSuggestionRowId(null), 200)
                        }
                      />

                      {/* Spinner */}
                      {isSearchingSku && activeSuggestionRowId === row.id && (
                        <div className="input-loading-spinner"></div>
                      )}

                      {/* Dropdown Menu */}
                      {activeSuggestionRowId === row.id &&
                        skuSuggestions.length > 0 &&
                        row.column === "sku_id" && (
                          <div className="suggestions-dropdown">
                            {skuSuggestions.map((item, idx) => (
                              <div
                                key={idx}
                                className="suggestion-item"
                                onMouseDown={() =>
                                  handleSelectSkuSuggestion(row.id, item.sku_id)
                                }
                              >
                                {item.sku_id}
                              </div>
                            ))}
                          </div>
                        )}
                    </div>

                    {/* Action Buttons */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        width: "100%",
                      }}
                    >
                      {/* Minus Button */}
                      {showMinus && (
                        <button
                          onClick={() => removeSkuFilterRow(row.id)}
                          className="icon-action-btn"
                          title="Remove filter"
                        >
                          {iconMinus}
                        </button>
                      )}

                      {/* Plus Button */}
                      <button
                        onClick={addSkuFilterRow}
                        className="icon-action-btn"
                        title="Add another filter"
                      >
                        {iconPlus}
                      </button>

                      {/* Apply & Reset Buttons */}
                      {isLast && (
                        <>
                          <button
                            onClick={handleApplySkuFilters}
                            className="filter-btn"
                            style={{ marginLeft: "10px" }}
                          >
                            Apply Filter
                          </button>
                          <button
                            onClick={resetSkuFilters}
                            className="filter-btn"
                            style={{ marginLeft: "10px" }}
                          >
                            Reset
                          </button>

                          <div
                            className="split-btn-container"
                            style={{ marginLeft: "auto" }}
                          >
                            <button
                              onClick={handleDownloadSku}
                              className={`split-main-btn ${
                                isDownloadingSku ? "loading" : ""
                              }`}
                              disabled={isDownloadingSku}
                              title="Download XLSX"
                            >
                              <div className="btn-icon">{iconDownload}</div>
                              <span>Download</span>
                            </button>
                            <button
                              className="split-arrow-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                setIsSkuDownloadDropdownOpen(
                                  !isSkuDownloadDropdownOpen,
                                );
                              }}
                            >
                              <svg width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg"
                                style={{ transform: isSkuDownloadDropdownOpen ? "rotate(-180deg)" : "rotate(0deg)", transition: "transform 0.2s ease" }}>
                                <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </button>
                            {isSkuDownloadDropdownOpen && (
                              <div className="split-dropdown-menu">
                                <button
                                  onClick={handleDownloadSkuUnfiltered}
                                  className={`split-dropdown-item ${isDownloadingSku ? "loading" : ""}`}
                                  disabled={isDownloadingSku}
                                >
                                  <div className="btn-icon" style={{ width: "16px", height: "16px" }}>{iconDownload}</div>
                                  <span>Download without filters</span>
                                </button>
                              </div>
                            )}
                          </div>

                          <div
                            className="column-visibility-container"
                            style={{ marginLeft: "10px" }}
                            ref={skuColsRef}
                          >
                            <button
                              className="toggle-btn column-toggle-btn"
                              onClick={() =>
                                setIsSkuColumnDropdownOpen(
                                  !isSkuColumnDropdownOpen,
                                )
                              }
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                fill="#f5f5f5"
                                viewBox="0 0 256 256"
                              >
                                <path d="M84,52V92H28a4,4,0,0,1-4-4V64A16,16,0,0,1,40,48H80A4,4,0,0,1,84,52Zm16,152a4,4,0,0,0,4,4h48a4,4,0,0,0,4-4V164H100ZM24,168v24a16,16,0,0,0,16,16H80a4,4,0,0,0,4-4V164H28A4,4,0,0,0,24,168Zm0-56v32a4,4,0,0,0,4,4H84V108H28A4,4,0,0,0,24,112ZM152,48H104a4,4,0,0,0-4,4V92h56V52A4,4,0,0,0,152,48Zm76,60H172v40h56a4,4,0,0,0,4-4V112A4,4,0,0,0,228,108ZM100,148h56V108H100ZM216,48H176a4,4,0,0,0-4,4V92h56a4,4,0,0,0,4-4V64A16,16,0,0,0,216,48Zm12,116H172v40a4,4,0,0,0,4,4h40a16,16,0,0,0,16-16V168A4,4,0,0,0,228,164Z"></path>
                              </svg>
                              <span>COLUMNS</span>
                            </button>
                            {isSkuColumnDropdownOpen && (
                              <div className="column-dropdown show">
                                {skuColumnOrder.map((colName, index) => (
                                  <div
                                    key={colName}
                                    className="column-dropdown-item"
                                    draggable="true"
                                    onDragStart={(e) => {
                                      e.dataTransfer.setData(
                                        "draggedIdx",
                                        index,
                                      );
                                      e.currentTarget.classList.add("dragging");
                                    }}
                                    onDragEnd={(e) => {
                                      e.currentTarget.classList.remove(
                                        "dragging",
                                      );
                                    }}
                                    onDragOver={(e) => {
                                      e.preventDefault();
                                      e.currentTarget.classList.add(
                                        "drag-over",
                                      );
                                    }}
                                    onDragLeave={(e) => {
                                      e.currentTarget.classList.remove(
                                        "drag-over",
                                      );
                                    }}
                                    onDrop={(e) => {
                                      e.preventDefault();
                                      e.currentTarget.classList.remove(
                                        "drag-over",
                                      );
                                      const draggedIdx =
                                        e.dataTransfer.getData("draggedIdx");
                                      handleSkuOnDrop(
                                        parseInt(draggedIdx),
                                        index,
                                      );
                                    }}
                                    style={{ cursor: "grab" }}
                                  >
                                    <span style={{ flex: 1 }}>{colName}</span>
                                    <div
                                      style={{ display: "flex", gap: "5px" }}
                                    >
                                      {/* Pulsante VISIBILITÀ */}
                                      <div
                                        className="column-visibility-icon"
                                        onClick={() =>
                                          setSkuColumnVisibility((prev) => ({
                                            ...prev,
                                            [colName]: !prev[colName],
                                          }))
                                        }
                                        style={{
                                          cursor: "pointer",
                                        }}
                                      >
                                        {skuColumnVisibility[colName]
                                          ? iconVisible
                                          : iconHidden}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="main-table-container full-page-table-sku">
              <table className="main-table">
                <thead>
                  <tr>
                    <SortableHeader
                      label="SKU ID"
                      sortKey="sku_id"
                      onSort={requestSkuSort}
                      sortIndicator={getSkuSortIndicator("sku_id")}
                    />
                    <SortableHeader
                      label="SKU desc."
                      sortKey="sku_description"
                      onSort={requestSkuSort}
                      sortIndicator={getSkuSortIndicator("sku_description")}
                    />

                    {skuColumnOrder.map((colName) => {
                      if (!skuColumnVisibility[colName]) return null;

                      const def = SKU_COLUMN_DEFS[colName];
                      if (!def) return null;

                      return (
                        <SortableHeader
                          key={colName}
                          label={colName}
                          sortKey={def.sortKey}
                          onSort={requestSkuSort}
                          sortIndicator={getSkuSortIndicator(def.sortKey)}
                        />
                      );
                    })}
                  </tr>
                </thead>

                <tbody
                  style={{
                    opacity: isSkuLoading ? 0.5 : 1,
                    transition: "opacity 0.2s ease-in-out",
                  }}
                >
                  {isSkuLoading && skuData.length === 0 ? (
                    <tr>
                      <td
                        colSpan="16"
                        style={{ textAlign: "center", padding: "2rem" }}
                      >
                        Loading SKU data...
                      </td>
                    </tr>
                  ) : skuViewMode === "unconstrained" && !hasSkuUnconstrained ? (
                    <tr>
                      <td
                        colSpan={2 + skuColumnOrder.filter((c) => skuColumnVisibility[c]).length}
                        style={{ textAlign: "center", padding: "2rem", color: "#8899aa" }}
                      >
                        Unconstrained data not yet available
                      </td>
                    </tr>
                  ) : (
                    effectiveSkuData.map((row, idx) => {
                      const cRow = skuData.find(
                        (r) => r.sku_id === row.sku_id,
                      ) || row;
                      const uRow = unconstrainedSkuMap.get(row.sku_id);
                      const baseRow =
                        skuViewMode === "constrained" ? cRow : uRow;
                      const targetRow =
                        skuViewMode === "constrained" ? uRow : cRow;
                      return (
                        <tr key={`${row.sku_id}-${idx}`}>
                          <td className="highlight-link">
                            <Link to={`/sku/${encodeURIComponent(row.sku_id)}`}>
                              {row.sku_id}
                            </Link>
                          </td>
                          <td>{row.sku_description}</td>

                          {skuColumnOrder.map((colName) => {
                            if (!skuColumnVisibility[colName]) return null;

                            const def = SKU_COLUMN_DEFS[colName];
                            if (!def) return <td key={colName}>Error</td>;

                            const cellClass = def.className
                              ? def.className(row)
                              : "";

                            return (
                              <td
                                key={colName}
                                className={`${cellClass}${isSkuCompareActive ? " compare-mode-cell" : ""}`}
                              >
                                {isSkuCompareActive
                                  ? renderCompareCell(
                                      def,
                                      baseRow,
                                      targetRow,
                                      formatPercentage,
                                      formatCurrency,
                                      formatNumber,
                                      formatUnitCurrency,
                                    )
                                  : def.render(
                                      row,
                                      formatPercentage,
                                      formatCurrency,
                                      formatNumber,
                                      formatUnitCurrency,
                                    )}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
            <Pagination
              currentPage={skuCurrentPage}
              totalPages={skuTotalPages}
              onPageChange={handleSkuPageChange}
            />
          </section>

          <section className="forecast-section">
            <h2 className="section-title-container">FORECAST</h2>
            <div className="forecast-mode-tabs">
              <button
                className={`forecast-mode-tab ${forecastMode === "demand" ? "active" : ""}`}
                onClick={() => { setForecastMode("demand"); setIsCompareActive(false); setForecastSortConfig({ key: null, direction: null }); }}
              >
                Demand
              </button>
              <button
                className={`forecast-mode-tab ${forecastMode === "winrate" ? "active" : ""}`}
                onClick={() => { setForecastMode("winrate"); setIsCompareActive(false); setForecastSortConfig({ key: null, direction: null }); }}
              >
                Win Rate
              </button>
            </div>
            <div className="filter-section forecast-controls">
              <span>Generation</span>
              <select
                id="generation-filter"
                value={forecastMode === "demand" ? selectedGenerationIndex : selectedWinrateGenIndex}
                onChange={(e) => {
                  const newIndex = parseInt(e.target.value, 10);
                  if (forecastMode === "demand") {
                    setSelectedGenerationIndex(newIndex);
                    if (newIndex === 0) setIsCompareActive(false);
                    if (availableRuns[newIndex]) {
                      setForecastData(allForecastData.filter((row) => row.run_id === availableRuns[newIndex].run_id));
                    } else {
                      setForecastData([]);
                    }
                  } else {
                    setSelectedWinrateGenIndex(newIndex);
                    if (newIndex === 0) setIsCompareActive(false);
                    if (winrateRuns[newIndex]) {
                      setWinrateData(allWinrateData.filter((row) => row.run_id === winrateRuns[newIndex].run_id));
                    } else {
                      setWinrateData([]);
                    }
                  }
                }}
              >
                {(forecastMode === "demand" ? availableRuns : winrateRuns).map((run, index) => {
                  const dateObj = new Date(run.run_date);
                  const label = `${formatPeriod(dateObj.toISOString().slice(0, 10))}, ${dateObj.toLocaleTimeString("en-US", {
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: true,
                    timeZone: "UTC",
                  })}`;
                  return (
                    <option key={run.run_id} value={index}>{label}</option>
                  );
                })}
                {(forecastMode === "demand" ? availableRuns : winrateRuns).length === 0 && (
                  <option value={0}>No Data</option>
                )}
              </select>

              <span>Customer</span>
              <select
                value={customerType}
                onChange={(e) => setCustomerType(e.target.value)}
              >
                <option value="combined">Combined</option>
                <option value="prime">Prime</option>
                <option value="reseller">Reseller</option>
              </select>

              {forecastMode === "winrate" && (
                <>
                  <span>Type</span>
                  <div className="availability-group">
                    <select
                      value={availabilityType}
                      onChange={(e) => setAvailabilityType(e.target.value)}
                    >
                      <option value="available">Available</option>
                      <option value="unavailable">Unavailable</option>
                    </select>
                    <label
                      className="compare-switch"
                      title={
                        availabilityType === "available"
                          ? "Compare Available → Unavailable"
                          : "Compare Unavailable → Available"
                      }
                    >
                      <input
                        type="checkbox"
                        checked={isWinrateCompareActive}
                        onChange={() => setIsWinrateCompareActive((prev) => !prev)}
                      />
                      <span className="compare-slider" />
                      <span className="compare-switch-label">
                        vs {availabilityType === "available" ? "Unavailable" : "Available"}
                      </span>
                    </label>
                  </div>
                </>
              )}

              <div className="compare-btn-wrapper">
                <button
                  id="compare-toggle-btn"
                  className={`toggle-btn ${isCompareActive ? "active" : ""}`}
                  disabled={forecastMode === "demand" ? selectedGenerationIndex === 0 : selectedWinrateGenIndex === 0}
                  onClick={() => setIsCompareActive(!isCompareActive)}
                >
                  Compare to Current
                </button>
              </div>

              <div className="toggle-container">
                {forecastMode === "demand" ? (
                  <div className="split-btn-container" style={{ marginRight: "10px", marginLeft: "10px" }}>
                    <button onClick={handleDownloadForecast}
                      className={`split-main-btn ${isDownloadingForecast ? "loading" : ""}`}
                      disabled={isDownloadingForecast} title="Download Family Forecast">
                      <div className="btn-icon">{iconDownload}</div>
                      <span>Download</span>
                    </button>
                    <button className="split-arrow-btn" onClick={(e) => { e.stopPropagation(); setIsDownloadDropdownOpen(!isDownloadDropdownOpen); }}>
                      <svg width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg"
                        style={{ transform: isDownloadDropdownOpen ? "rotate(-180deg)" : "rotate(0deg)", transition: "transform 0.2s ease" }}>
                        <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                    {isDownloadDropdownOpen && (
                      <div className="split-dropdown-menu">
                        <button
                          onClick={handleDownloadForecastUnfiltered}
                          className={`split-dropdown-item ${isDownloadingForecast ? "loading" : ""}`}
                          disabled={isDownloadingForecast || availableRuns.length === 0}
                        >
                          <div className="btn-icon" style={{ width: "16px", height: "16px" }}>{iconDownload}</div>
                          <span>Download without filters</span>
                        </button>
                        <button onClick={() => handleDownloadAllSkus()}
                          className={`split-dropdown-item ${isDownloadingAllSkus ? "loading" : ""}`}
                          disabled={isDownloadingAllSkus || availableRuns.length === 0}>
                          <div className="btn-icon" style={{ width: "16px", height: "16px" }}>{iconDownload}</div>
                          <span>Download all SKUs without filters</span>
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="split-btn-container" style={{ marginRight: "10px", marginLeft: "10px" }}>
                    <button onClick={handleDownloadWinrate}
                      className={`split-main-btn ${isDownloadingWinrate ? "loading" : ""}`}
                      disabled={isDownloadingWinrate} title="Download Win Rate Forecast">
                      <div className="btn-icon">{iconDownload}</div>
                      <span>Download</span>
                    </button>
                    <button className="split-arrow-btn" onClick={(e) => { e.stopPropagation(); setIsWinrateDropdownOpen(!isWinrateDropdownOpen); }}>
                      <svg width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg"
                        style={{ transform: isWinrateDropdownOpen ? "rotate(-180deg)" : "rotate(0deg)", transition: "transform 0.2s ease" }}>
                        <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                    {isWinrateDropdownOpen && (
                      <div className="split-dropdown-menu">
                        <button
                          onClick={handleDownloadWinrateUnfiltered}
                          className={`split-dropdown-item ${isDownloadingWinrate ? "loading" : ""}`}
                          disabled={isDownloadingWinrate || winrateRuns.length === 0}
                        >
                          <div className="btn-icon" style={{ width: "16px", height: "16px" }}>{iconDownload}</div>
                          <span>Download without filters</span>
                        </button>
                        <button onClick={() => handleDownloadAllSkusWinrate()}
                          className={`split-dropdown-item ${isDownloadingAllSkusWinrate ? "loading" : ""}`}
                          disabled={isDownloadingAllSkusWinrate || winrateRuns.length === 0}>
                          <div className="btn-icon" style={{ width: "16px", height: "16px" }}>{iconDownload}</div>
                          <span>Download all SKUs without filters</span>
                        </button>
                      </div>
                    )}
                  </div>
                )}

                <div className="column-visibility-container" ref={forecastColsRef}>
                  <button id="column-toggle-btn" className="toggle-btn column-toggle-btn"
                    onClick={() => setIsColumnDropdownOpen(!isColumnDropdownOpen)}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="#f5f5f5" viewBox="0 0 256 256">
                      <path d="M84,52V92H28a4,4,0,0,1-4-4V64A16,16,0,0,1,40,48H80A4,4,0,0,1,84,52Zm16,152a4,4,0,0,0,4,4h48a4,4,0,0,0,4-4V164H100ZM24,168v24a16,16,0,0,0,16,16H80a4,4,0,0,0,4-4V164H28A4,4,0,0,0,24,168Zm0-56v32a4,4,0,0,0,4,4H84V108H28A4,4,0,0,0,24,112ZM152,48H104a4,4,0,0,0-4,4V92h56V52A4,4,0,0,0,152,48Zm76,60H172v40h56a4,4,0,0,0,4-4V112A4,4,0,0,0,228,108ZM100,148h56V108H100ZM216,48H176a4,4,0,0,0-4,4V92h56a4,4,0,0,0,4-4V64A16,16,0,0,0,216,48Zm12,116H172v40a4,4,0,0,0,4,4h40a16,16,0,0,0,16-16V168A4,4,0,0,0,228,164Z"></path>
                    </svg>
                    <span>COLUMNS</span>
                  </button>
                  {isColumnDropdownOpen && (
                    <div id="column-dropdown" className="column-dropdown show">
                      {(forecastMode === "demand" ? forecastColumns : winrateColumns)
                        .slice(1).filter((col) => col !== "ACT QTY")
                        .map((colName) => (
                          <div key={colName} className="column-dropdown-item">
                            <span>{colName}</span>
                            <div className="column-visibility-icon"
                              onClick={() => {
                                if (forecastMode === "demand") {
                                  setColumnVisibility((prev) => ({ ...prev, [colName]: !prev[colName] }));
                                } else {
                                  setWinrateColumnVisibility((prev) => ({ ...prev, [colName]: !prev[colName] }));
                                }
                              }}>
                              {(forecastMode === "demand" ? columnVisibility : winrateColumnVisibility)[colName]
                                ? iconVisible : iconHidden}
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {isForecastLoading ? (
              <div style={{ textAlign: "center", padding: "3rem", color: "#8899aa", fontSize: "1.1rem" }}>
                Loading forecast data...
              </div>
            ) : forecastMode === "demand" ? (
              <>
                {sortedForecastData.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "3rem", color: "#8899aa", fontSize: "1.1rem" }}>
                    No forecast data available
                  </div>
                ) : (
                <>
                <div className="main-table-container forecast-table-container">
                  <table className="main-table">
                    <thead>
                      <tr>
                        {forecastColumns.map((colName) =>
                          columnVisibility[colName] && (
                            <th key={colName} onClick={() => requestForecastSort(colName)}
                              style={{ cursor: "pointer", userSelect: "none" }}>
                              {colName}{getForecastSortIndicator(colName)}
                            </th>
                          ),
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {sortedForecastData.map((row, i) => (
                        <tr key={i}>
                          {columnVisibility["MONTH"] && <td>{formatPeriod(row.year_week)}</td>}
                          {columnVisibility["ACT QTY"] && <td>{formatNumber(row.actual_qty)}</td>}
                          {columnVisibility["ACT QTY Y-1"] && <td>{formatNumber(row.actual_qty_last_year)}</td>}
                          {columnVisibility["FCST QTY - σ"] && <td className="fcst-minus">{formatNumber(row.fcst_qty_lowerbound)}</td>}
                          {columnVisibility["FCST QTY (avg)"] && <td className="fcst-avg">{formatNumber(row.fcst_qty_avg)}</td>}
                          {columnVisibility["FCST QTY + σ"] && <td className="fcst-plus">{formatNumber(row.fcst_qty_upperbound)}</td>}
                          {columnVisibility["ACT VAL Y-1"] && <td>{formatCurrency(row.actual_val_last_year)}</td>}
                          {columnVisibility["FCST VALUE - σ"] && <td className="fcst-minus">{formatCurrency(row.fcst_val_lowerbound)}</td>}
                          {columnVisibility["FCST VALUE (avg)"] && <td className="fcst-avg">{formatCurrency(row.fcst_val_avg)}</td>}
                          {columnVisibility["FCST VALUE + σ"] && <td className="fcst-plus">{formatCurrency(row.fcst_val_upperbound)}</td>}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="forecast-graphs" key="demand-graphs">
                  <div className="plotly-chart" onClick={(e) => handleChartClick(e, "forecast", "qty", "Forecast QTY")}>
                    <Plot
                      data={getForecastChartConfig("qty", filteredForecastData, false, comparisonData).traces}
                      layout={getForecastChartConfig("qty", filteredForecastData, false, comparisonData).layout}
                      config={getForecastChartConfig("qty", filteredForecastData, false, comparisonData).config}
                      style={{ width: "100%", height: "100%" }}
                    />
                  </div>
                  <div className="plotly-chart" onClick={(e) => handleChartClick(e, "forecast", "value", "Forecast VALUE (at AVG COST)")}>
                    <Plot
                      data={getForecastChartConfig("value", filteredForecastData, false, comparisonData).traces}
                      layout={getForecastChartConfig("value", filteredForecastData, false, comparisonData).layout}
                      config={getForecastChartConfig("value", filteredForecastData, false, comparisonData).config}
                      style={{ width: "100%", height: "100%" }}
                    />
                  </div>
                </div>
                </>
                )}
              </>
            ) : (
              <>
                {sortedWinrateData.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "3rem", color: "#8899aa", fontSize: "1.1rem" }}>
                    No win rate data available
                  </div>
                ) : (
                <>
                <div className="main-table-container forecast-table-container">
                  <table className="main-table">
                    <thead>
                      <tr>
                        {winrateColumns.map((colName) =>
                          showWrCol(colName) && (
                            <th key={colName} onClick={() => requestForecastSort(colName)}
                              style={{ cursor: "pointer", userSelect: "none" }}>
                              {colName}{getForecastSortIndicator(colName)}
                            </th>
                          ),
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {sortedWinrateData.map((row, i) => (
                        <tr key={i}>
                          {showWrCol("MONTH") && <td>{formatPeriod(row.year_week)}</td>}
                          {showWrCol("ACT WR Y-1 RAW") && (
                            <td className={isWinrateCompareActive ? "compare-mode-cell" : ""}>{renderWinrateCell(row, "actual_wr_last_year")}</td>
                          )}
                          {showWrCol("FCST WR (avg) RAW") && (
                            <td className={`fcst-avg${isWinrateCompareActive ? " compare-mode-cell" : ""}`}>{renderWinrateCell(row, "fcst_qty_avg")}</td>
                          )}
                          {showWrCol("ACT WR Y-1 SHRINKED") && (
                            <td className={isWinrateCompareActive ? "compare-mode-cell" : ""}>{renderWinrateCell(row, "actual_wr_last_year_shrinked")}</td>
                          )}
                          {showWrCol("FCST WR (avg) SHRINKED") && (
                            <td className={`fcst-avg${isWinrateCompareActive ? " compare-mode-cell" : ""}`}>{renderWinrateCell(row, "fcst_qty_avg_shrinked")}</td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="forecast-graphs forecast-graphs--single" key="winrate-graphs">
                  <div className="plotly-chart" onClick={(e) => handleChartClick(e, "winrate", "winrate", "Win Rate Forecast")}>
                    <Plot
                      data={getWinrateChartConfig(filteredWinrateData, false, comparisonData).traces}
                      layout={getWinrateChartConfig(filteredWinrateData, false, comparisonData).layout}
                      config={getWinrateChartConfig(filteredWinrateData, false, comparisonData).config}
                      useResizeHandler={true}
                      style={{ width: "100%", height: "100%" }}
                    />
                  </div>
                </div>
                </>
                )}
              </>
            )}
          </section>
        </main>
      </div>

      {popupChart && (
        <div
          id="image-popup"
          className="image-popup-overlay"
          style={{ display: "flex" }}
          onClick={closePopup}
        >
          <span id="popup-close" className="popup-close" onClick={closePopup}>
            &times;
          </span>
          <div
            id="popup-chart-container"
            className="popup-content"
            onClick={(e) => e.stopPropagation()}
          >
            {(() => {
              const { type, name, title } = popupChart;
              let chartConfig;
              if (type === "kpi")
                chartConfig = getKpiChartConfig(
                  name,
                  familyKpiHistory,
                  familyConstraints,
                  true,
                  config,
                  unconstrainedFamilyKpiHistory,
                );
              else if (type === "forecast")
                chartConfig = getForecastChartConfig(
                  name,
                  filteredForecastData,
                  true,
                  comparisonData,
                );
              else if (type === "winrate")
                chartConfig = getWinrateChartConfig(
                  filteredWinrateData,
                  true,
                  comparisonData,
                );
              else return null;
              return (
                <Plot
                  data={chartConfig.traces}
                  layout={{
                    ...chartConfig.layout,
                    title: {
                      text: title,
                      font: { size: 20, color: "#333" },
                      x: 0.5,
                    },
                  }}
                  config={chartConfig.config}
                  style={{ width: "100%", height: "100%" }}
                />
              );
            })()}
          </div>
        </div>
      )}
    </>
  );
}

export default FamilyView;
